<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'

const props = defineProps({
  sidebarExpanded: {
    type: Boolean,
    default: false
  },
  query: {
    type: String,
    default: ''
  },
  savedSearchId: {
    type: String,
    default: null
  },
  savedSearchName: {
    type: String,
    default: ''
  },
  savedSearchDescription: {
    type: String,
    default: ''
  },
  savedSearchQuery: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  showTrigger: {
    type: Boolean,
    default: true
  },
  mode: {
    type: String,
    default: 'sidebar', // 'sidebar' | 'inline'
    validator: (v) => ['sidebar', 'inline'].includes(v)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  deleteLoading: {
    type: Boolean,
    default: false
  },
  // Subscription props
  isSubscribed: {
    type: Boolean,
    default: false
  },
  subscriptionLoading: {
    type: Boolean,
    default: false
  },
  defaultName: {
    type: String,
    default: ''
  },
  defaultDescription: {
    type: String,
    default: ''
  },
  anchorElement: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['save', 'overwrite', 'delete', 'close', 'subscribe', 'unsubscribe'])

const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})

// Form fields
const name = ref('')
const description = ref('')

// Delete confirmation state
const deleteConfirming = ref(false)

// Compute save state based on query/filters
const saveState = computed(() => {
  if (!props.savedSearchId) return 'unsaved'
  if (props.query === props.savedSearchQuery) return 'saved'
  return 'diverged'
})

// Check if form fields have been modified from saved values
const hasFormChanges = computed(() => {
  if (!props.savedSearchId) return false
  return name.value.trim() !== props.savedSearchName ||
         description.value.trim() !== props.savedSearchDescription
})

// State-based icon color class
const stateColorClass = computed(() => {
  switch (saveState.value) {
    case 'saved': return 'state-saved'
    case 'diverged': return 'state-diverged'
    default: return 'state-unsaved'
  }
})

// Button label based on state
const buttonLabel = computed(() => {
  switch (saveState.value) {
    case 'saved': return 'saved'
    case 'diverged': return 'modified'
    default: return 'save'
  }
})

// Position the menu based on mode
function updateMenuPosition() {
  const anchor = props.anchorElement?.$el || props.anchorElement || triggerRef.value
  if (!anchor || !isOpen.value) return

  const rect = anchor.getBoundingClientRect()
  const menuWidth = 300
  const menuHeight = menuRef.value?.offsetHeight || 280

  let left, top

  if (props.mode === 'inline') {
    // Inline mode: position below trigger, aligned right
    top = rect.bottom + 8
    left = rect.right - menuWidth

    // Check if dropdown would go off-screen bottom
    if (top + menuHeight > window.innerHeight - 10) {
      top = rect.top - menuHeight - 8
    }
  } else {
    // Sidebar mode: position to the left of the sidebar
    left = rect.left - menuWidth - 8
    if (left < 10) left = 10

    // Position ABOVE trigger (drop-up)
    top = rect.bottom - menuHeight
    if (top < 10) top = 10
  }

  // Keep within viewport horizontally
  if (left < 10) left = 10
  if (left + menuWidth > window.innerWidth - 10) {
    left = window.innerWidth - menuWidth - 10
  }

  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${menuWidth}px`
  }
}

function toggle() {
  if (props.disabled || props.loading || props.deleteLoading) return
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
  deleteConfirming.value = false
  emit('close')
}

function open() {
  isOpen.value = true
}

// Initialize form with saved search data when opening
watch(isOpen, async (newVal) => {
  if (newVal) {
    // Pre-fill with existing data if editing
    if (props.savedSearchId) {
      name.value = props.savedSearchName || ''
      description.value = props.savedSearchDescription || ''
    } else {
      name.value = props.defaultName || ''
      description.value = props.defaultDescription || ''
    }
    // Reset delete confirmation state
    deleteConfirming.value = false

    await nextTick()
    updateMenuPosition()
  }
})

// Reposition menu when savedSearchId changes (e.g., after saving - notifications section appears)
watch(() => props.savedSearchId, async (newVal, oldVal) => {
  if (isOpen.value && newVal !== oldVal) {
    await nextTick()
    updateMenuPosition()
  }
})

// Handle save action (no auto-close - user may want to set notifications)
function handleSave() {
  if (!name.value.trim()) return

  emit('save', {
    name: name.value.trim(),
    description: description.value.trim()
  })
}

// Handle overwrite action (no auto-close - user may want to continue editing)
function handleOverwrite() {
  if (!name.value.trim()) return

  emit('overwrite', {
    name: name.value.trim(),
    description: description.value.trim()
  })
}

// Handle delete actions
function handleDeleteClick() {
  deleteConfirming.value = true
}

function handleDeleteCancel() {
  deleteConfirming.value = false
}

function handleDeleteConfirm() {
  emit('delete')
  // Don't close here - parent will close after successful delete
}

// Toggle subscription
function toggleSubscription() {
  if (props.isSubscribed) {
    emit('unsubscribe')
  } else {
    emit('subscribe')
  }
}

// Close when clicking outside
function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) {
    return
  }
  if (menuRef.value && menuRef.value.contains(event.target)) {
    return
  }
  close()
}

// Close on escape
function handleKeydown(event) {
  if (event.key === 'Escape') {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})

defineExpose({ close, open, isOpen })
</script>

<template>
  <component :is="showTrigger ? 'li' : 'div'" class="nav-item-wrapper save-dropdown-wrapper" :class="{ 'inline-mode': mode === 'inline' }">
    <!-- Sidebar trigger button -->
    <div
      v-if="showTrigger"
      ref="triggerRef"
      class="sidebar-nav"
      :class="[stateColorClass, { 'active': isOpen, 'loading': loading, 'disabled': disabled }]"
      @click="toggle"
    >
      <font-awesome-icon class="icon" :icon="['fas', 'save']" />
      <span class="nav-text">{{ buttonLabel }}</span>
      <div class="nav-popout" v-if="!sidebarExpanded && !isOpen">
        <span>{{ buttonLabel }}</span>
      </div>
    </div>
    <!-- Inline trigger button for edit mode -->
    <button
      v-else
      ref="triggerRef"
      class="btn-edit-trigger"
      :class="{ 'is-open': isOpen }"
      title="Edit search"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', 'pen']" />
    </button>

    <!-- Menu (teleported to body) -->
    <Teleport to="body">
      <Transition name="save-dropdown">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="save-dropdown-menu"
          :style="menuStyle"
        >
          <!-- Loading overlay -->
          <div v-if="loading || deleteLoading" class="save-dropdown-loading">
            <div class="spinner"></div>
            <span>{{ deleteLoading ? 'Deleting...' : 'Saving...' }}</span>
          </div>

          <!-- Header -->
          <div class="save-dropdown-header">
            <font-awesome-icon class="header-icon" :icon="['fas', mode === 'inline' ? 'pen' : 'save']" />
            <div class="header-text">
              <span class="header-label">{{ mode === 'inline' ? 'Edit Search' : 'Save Search' }}</span>
              <span class="header-subtext">
                {{ mode === 'inline' ? 'Update name and description' : (saveState === 'unsaved' ? 'Save current query' : 'Update or save as new') }}
              </span>
            </div>
          </div>

          <!-- Form content -->
          <div class="save-dropdown-content" :class="{ 'is-loading': loading || deleteLoading }">
            <div class="form-group">
              <label for="search-name">Name</label>
              <input
                id="search-name"
                v-model="name"
                type="text"
                placeholder="My saved search"
                maxlength="255"
                @keydown.enter.prevent="saveState === 'diverged' ? handleOverwrite() : handleSave()"
              />
            </div>

            <div class="form-group">
              <label for="search-description">Description <span class="optional">(optional)</span></label>
              <textarea
                id="search-description"
                v-model="description"
                placeholder="Describe what this search is for..."
                maxlength="1000"
                rows="2"
              ></textarea>
            </div>

            <!-- Action buttons -->
            <div class="save-dropdown-actions">
              <template v-if="saveState === 'diverged'">
                <button
                  class="btn btn-secondary"
                  :disabled="!name.trim() || loading"
                  @click="handleSave"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                  Save as New
                </button>
                <button
                  class="btn btn-primary"
                  :disabled="!name.trim() || loading"
                  @click="handleOverwrite"
                >
                  <font-awesome-icon :icon="['fas', 'sync']" />
                  Overwrite
                </button>
              </template>
              <template v-else-if="saveState === 'saved' && hasFormChanges">
                <button
                  class="btn btn-primary full-width"
                  :disabled="!name.trim() || loading"
                  @click="handleOverwrite"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  Update
                </button>
              </template>
              <template v-else-if="saveState !== 'saved'">
                <button
                  class="btn btn-primary full-width"
                  :disabled="!name.trim() || loading"
                  @click="handleSave"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  Save Search
                </button>
              </template>
            </div>

            <!-- Subscription section (only when search is saved, not shown in inline mode) -->
            <div v-if="savedSearchId && mode !== 'inline'" class="subscription-section">
              <span class="subscription-text">
                {{ isSubscribed ? 'Receiving notifications' : 'Get notified on new results' }}
              </span>
              <button
                class="btn-bell"
                :class="{ 'is-subscribed': isSubscribed }"
                :disabled="subscriptionLoading"
                :title="isSubscribed ? 'Unsubscribe from notifications' : 'Subscribe to notifications'"
                @click="toggleSubscription"
              >
                <font-awesome-icon
                  v-if="subscriptionLoading"
                  icon="spinner"
                  spin
                />
                <font-awesome-icon
                  v-else
                  :icon="isSubscribed ? ['fas', 'bell'] : ['far', 'bell']"
                />
              </button>
            </div>

            <!-- Delete section (only when search is saved) -->
            <div v-if="savedSearchId" class="delete-section">
              <template v-if="!deleteConfirming">
                <button
                  class="btn-delete"
                  :disabled="loading || deleteLoading"
                  @click="handleDeleteClick"
                >
                  <font-awesome-icon :icon="['fas', 'trash']" />
                  Delete search
                </button>
              </template>
              <template v-else>
                <div class="delete-confirm">
                  <span class="delete-confirm-text">Delete this search?</span>
                  <div class="delete-confirm-actions">
                    <button
                      class="btn-delete-cancel"
                      :disabled="deleteLoading"
                      @click="handleDeleteCancel"
                    >
                      Cancel
                    </button>
                    <button
                      class="btn-delete-confirm"
                      :disabled="deleteLoading"
                      @click="handleDeleteConfirm"
                    >
                      <font-awesome-icon v-if="deleteLoading" icon="spinner" spin />
                      <span v-else>Delete</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </component>
</template>

<style scoped>
.save-dropdown-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

.sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  border-radius: 0.75rem;
  position: relative;
  gap: 0;
  cursor: pointer;
}

.sidebar-nav.disabled {
  color: #4a5568;
  cursor: not-allowed;
}

.sidebar-nav.loading {
  opacity: 0.6;
  cursor: wait;
}

.sidebar-nav.active {
  background-color: #1d2531;
  color: white;
}

.sidebar-nav:not(.loading):not(.disabled):hover {
  color: white;
  background-color: #1d2531;
}

.sidebar-nav.disabled:hover {
  background-color: #1a1f28;
}

/* State colors */
.sidebar-nav.state-unsaved {
  color: #7A8696;
}

.sidebar-nav.state-unsaved:not(.disabled):hover,
.sidebar-nav.state-unsaved.active {
  color: white;
}

.sidebar-nav.state-saved {
  color: #10B981;
}

.sidebar-nav.state-saved .icon {
  color: #10B981;
}

.sidebar-nav.state-diverged {
  color: #F59E0B;
}

/* Disabled overrides state colors */
.sidebar-nav.disabled,
.sidebar-nav.disabled .icon {
  color: #4a5568;
}

.sidebar-nav.state-diverged .icon {
  color: #F59E0B;
}

.sidebar-nav .icon {
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-text {
  display: none;
}

.nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: #1d2531;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

.save-dropdown-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

/* Muted popout for disabled items */
.sidebar-nav.disabled .nav-popout {
  background: #1a1f28;
  color: #4a5568;
}

.sidebar-nav.active .nav-popout {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>

<!-- Styles for expanded sidebar state -->
<style>
#tool-sidebar.expanded .save-dropdown-wrapper .sidebar-nav {
  justify-content: flex-start;
}

#tool-sidebar.expanded .save-dropdown-wrapper .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

#tool-sidebar.expanded .save-dropdown-wrapper .sidebar-nav .icon {
  margin-right: 0.75rem;
}

#tool-sidebar:not(.expanded) .save-dropdown-wrapper .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

/* Dropdown menu styles */
.save-dropdown-menu {
  background: #1a1f28;
  border: 1px solid #2a3441;
  border-radius: 0.75rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  z-index: 9999;
  overflow: hidden;
  min-width: 280px;
}

.save-dropdown-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.02);
}

.save-dropdown-header .header-icon {
  font-size: 1rem;
  color: #7357D2;
  width: 1.2rem;
  flex-shrink: 0;
}

.save-dropdown-header .header-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.save-dropdown-header .header-label {
  font-size: 0.95rem;
  color: #E5E7EB;
  font-weight: 500;
}

.save-dropdown-header .header-subtext {
  font-size: 0.75rem;
  color: #6B7280;
}

.save-dropdown-content {
  position: relative;
  padding: 1rem;
}

.save-dropdown-content.is-loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Form styles */
.form-group {
  margin-bottom: 0.75rem;
}

.form-group label {
  display: block;
  font-size: 0.8rem;
  color: #9CA3AF;
  margin-bottom: 0.35rem;
}

.form-group .optional {
  color: #6B7280;
  font-weight: normal;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: #151a22;
  border: 1px solid #2a3441;
  border-radius: 0.5rem;
  color: #E5E7EB;
  font-size: 0.85rem;
  transition: border-color 0.15s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #7357D2;
}

.form-group input::placeholder,
.form-group textarea::placeholder {
  color: #4B5563;
}

.form-group textarea {
  resize: none;
  min-height: 50px;
}

/* Action buttons */
.save-dropdown-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.save-dropdown-actions .btn {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.15s ease;
}

.save-dropdown-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-dropdown-actions .btn-primary {
  background: #7357D2;
  color: white;
}

.save-dropdown-actions .btn-primary:hover:not(:disabled) {
  background: #8568e3;
}

.save-dropdown-actions .btn-secondary {
  background: #374151;
  color: #E5E7EB;
}

.save-dropdown-actions .btn-secondary:hover:not(:disabled) {
  background: #4B5563;
}

.save-dropdown-actions .btn.full-width {
  flex: none;
  width: 100%;
}

/* Subscription section */
.subscription-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.subscription-text {
  font-size: 0.8rem;
  color: #9CA3AF;
}

.btn-bell {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #7A8696;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-bell:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.3);
  color: #F59E0B;
}

.btn-bell.is-subscribed {
  background: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.3);
  color: #F59E0B;
}

.btn-bell.is-subscribed:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #EF4444;
}

.btn-bell:disabled {
  opacity: 0.5;
  cursor: wait;
}

/* Loading overlay */
.save-dropdown-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 31, 40, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 10;
  color: #9CA3AF;
  font-size: 0.85rem;
}

.save-dropdown-loading .spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #374151;
  border-top: 2px solid #7357D2;
  border-radius: 50%;
  animation: save-dropdown-spin 1s linear infinite;
}

@keyframes save-dropdown-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dropdown animation - slides up */
.save-dropdown-enter-active,
.save-dropdown-leave-active {
  transition: all 0.2s ease;
}

.save-dropdown-enter-from,
.save-dropdown-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Inline mode styles */
.save-dropdown-wrapper.inline-mode {
  padding: 0;
  display: inline-flex;
}

/* Inline edit trigger button */
.btn-edit-trigger {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #7A8696;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #E5E7EB;
}

.btn-edit-trigger.is-open {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #E5E7EB;
}

/* Delete section */
.delete-section {
  margin-top: 1rem;
}

.btn-delete {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 0.5rem;
  color: #9CA3AF;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.15s ease;
}

.btn-delete:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #EF4444;
}

.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Delete confirmation */
.delete-confirm {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.delete-confirm-text {
  font-size: 0.8rem;
  color: #EF4444;
  text-align: center;
}

.delete-confirm-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-delete-cancel,
.btn-delete-confirm {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.15s ease;
}

.btn-delete-cancel {
  background: #374151;
  border: 1px solid #4B5563;
  color: #E5E7EB;
}

.btn-delete-cancel:hover:not(:disabled) {
  background: #4B5563;
}

.btn-delete-confirm {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #EF4444;
  min-width: 80px;
}

.btn-delete-confirm:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
}

.btn-delete-cancel:disabled,
.btn-delete-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
